import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMountedState } from 'react-use';
import { findIndex, get, isEqual, map, some } from 'lodash';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import {
    COMMON_FLAGS,
    FEATURE_FLAGS,
    useCommonConfig,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { aiGenerationApi, ERROR_CODES } from 'services/api';
import { COMPONENT_STATUS } from 'services/constants';
import { CROPPING_MODAL_COMPONENT_NAME, OUTCROPPING_COMPONENT_NAME } from '../constants/croppingModalGtmConstants';
import { createImage } from '../utils/getCropImage';
import { useCropDataInit } from './useCropDataInit';
import { useFilterDefaultCropData } from './useFilterDefaultCropData';

export const sendCropGTMEvent = value => {
    gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
        component: CROPPING_MODAL_COMPONENT_NAME,
        value,
    });
};

export const useCroppingToolsFormState = ({ onClose, originalSrc, cropInit, isVerticalMedia }) => {
    const { value: cropData } = useFormFieldValue({
        field: 'cropData',
    });
    const filteredDefaultCropData = useFilterDefaultCropData({ isVerticalMedia });
    const ratios = useMemo(() => map(filteredDefaultCropData, 'ratio'), [filteredDefaultCropData]);

    const [srcHistory, setSrcHistory] = useState([]);
    const [shouldShowOutcropBanner, setShouldShowOutcropBanner] = useState(false);
    const [activeRatio, setActiveRatio] = useState(ratios[0]);
    const [outcroppingStatus, setOutcroppingStatus] = useState(COMPONENT_STATUS.INITIAL);
    const [outcroppingError, setOutcroppingError] = useState(null);
    const [src, setSrc] = useState(originalSrc);
    const curSrcIndex = srcHistory.indexOf(src);
    const isMounted = useMountedState();

    useCropDataInit({ src, ratios, setOutcroppingStatus, cropInit, defaultCropData: filteredDefaultCropData });

    const isOutcroppingEnabled = useConfigMatch({ [FEATURE_FLAGS.OUTCROPPING_IMAGE_ENABLED]: 'true' });

    const shouldShowOutcropWarning =
        !isVerticalMedia && shouldShowOutcropBanner && outcroppingStatus !== COMPONENT_STATUS.LOADING;

    const shouldRenderOutcropBanner =
        isOutcroppingEnabled && shouldShowOutcropBanner && outcroppingStatus !== COMPONENT_STATUS.LOADING;

    const {
        formAccount: { accountId },
    } = useFormDataContext();

    const {
        [COMMON_FLAGS.OUTCROPPING_IMAGE_NUM_IMAGES]: outcropNumImages,
        [COMMON_FLAGS.OUTCROPPING_IMAGE_MIN_THRESHOLD]: outcropRatioMinThreshold,
        [COMMON_FLAGS.OUTCROPPING_IMAGE_MAX_THRESHOLD]: outcropRatioMaxThreshold,
    } = useCommonConfig([
        COMMON_FLAGS.OUTCROPPING_IMAGE_NUM_IMAGES,
        COMMON_FLAGS.OUTCROPPING_IMAGE_MIN_THRESHOLD,
        COMMON_FLAGS.OUTCROPPING_IMAGE_MAX_THRESHOLD,
    ]);

    const onApply = useCallback(() => {
        if (curSrcIndex >= 0) {
            gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                component: `${OUTCROPPING_COMPONENT_NAME}: Save Image Clicked`,
                value: src,
            });
        }

        onClose({
            cropData,
            src,
        });
        sendCropGTMEvent('apply crop');
        return cropData;
    }, [curSrcIndex, onClose, src, cropData]);

    const onSkip = useCallback(() => {
        onClose({ cropData: null, src: originalSrc });
        sendCropGTMEvent('skip modal');
    }, [onClose, originalSrc]);

    const activeIndex = useMemo(() => findIndex(cropData, { ratio: activeRatio }), [activeRatio, cropData]);

    const cropInitMap = useMemo(() => {
        return cropInit?.cropData?.reduce((map, crop) => {
            const ratioValue = crop.ratio.width / crop.ratio.height;
            map[ratioValue] = crop;
            return map;
        }, {});
    }, [cropInit]);

    const isEqualToInitialData = useMemo(() => {
        return !some(cropData, crop => {
            // is replaced image changed in edit flow
            if (cropInitMap && cropInitMap[crop.ratio]) {
                if (cropInitMap[crop.ratio].url !== crop.replacedImageUrl) {
                    return true;
                }
            }
            // is replaced image changed in create flow
            else if (crop.replacedImageUrl) {
                return true;
            }

            // no replaced image
            return (
                crop.croppedAreaPixels !== null ||
                (!crop.isFromCloudinary &&
                    isEqual(crop.initialCroppedAreaPixels, crop.cloudinaryInitialCroppedAreaPixels))
            );
        });
    }, [cropData, cropInitMap]);

    const updateSrc = useCallback(async newSrc => {
        setSrc(newSrc);
    }, []);

    const onOutcropImage = async (apiBody = {}) => {
        setOutcroppingStatus(COMPONENT_STATUS.LOADING);
        setOutcroppingError(null);

        let results;

        try {
            const response = await aiGenerationApi.getAIGeneratedOutcropImage({
                accountId,
                campaignId: AllEntityId,
                imageUrl: src,
                numImages: outcropNumImages,
                ...apiBody,
            });
            results = get(response, 'results', []).map(({ url }) => url);
        } catch (err) {
            if (!isMounted()) {
                return;
            }
            setOutcroppingStatus(COMPONENT_STATUS.ERROR);

            if (err?.status === ERROR_CODES.UNPROCESSABLE_ENTITY) {
                setOutcroppingError('app.genai.againstpolicy.error');
            } else {
                setOutcroppingError('creative.studio.error.generic');
            }

            return;
        }
        if (!isMounted()) {
            return;
        }

        // if the API returned no results, show an error
        if (!results?.length) {
            setOutcroppingStatus(COMPONENT_STATUS.ERROR);
            setOutcroppingError('creative.studio.error.generic');

            return;
        }

        updateSrc(results[0]);
        setSrcHistory(results);
        setOutcroppingStatus(COMPONENT_STATUS.ACTIVE);
        setShouldShowOutcropBanner(false);
    };

    const checkSrcRatio = useCallback(async () => {
        try {
            const { width, height } = await createImage(src);

            if (!isMounted()) {
                return;
            }

            const srcRatio = parseFloat(width / height);

            let isBeyondThreshold;

            if (outcropRatioMinThreshold) {
                isBeyondThreshold = srcRatio < outcropRatioMinThreshold;
            }

            if (outcropRatioMaxThreshold) {
                isBeyondThreshold = isBeyondThreshold || srcRatio > outcropRatioMaxThreshold;
            }

            setShouldShowOutcropBanner(isBeyondThreshold);

            if (isBeyondThreshold) {
                gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
                    component: `${OUTCROPPING_COMPONENT_NAME}: Warning Visible`,
                    value: src,
                });
            }
        } catch (err) {
            if (!isMounted()) {
                return;
            }

            setShouldShowOutcropBanner(false);
        }
    }, [outcropRatioMaxThreshold, outcropRatioMinThreshold, src, isMounted]);

    useEffect(() => {
        // if outcropping is disabled OR if the src is not the original src OR if the src is a stable outcropping image
        //       don't check the ratio
        const shouldSkipRatioCheck =
            !isOutcroppingEnabled || originalSrc !== src || /.*STABLE_DIFFUSION_OUTCROP.*/.test(originalSrc);

        if (shouldSkipRatioCheck) {
            return;
        }

        checkSrcRatio();
    }, [checkSrcRatio, isOutcroppingEnabled, originalSrc, src]);

    return {
        handleSubmit: onApply,
        handleSkip: onSkip,
        setActiveRatio,
        activeRatio,
        activeIndex,
        isEqualToInitialData,
        ratios,
        setSrcHistory,
        curSrcIndex,
        srcHistory,
        shouldShowOutcropBanner,
        outcroppingStatus,
        outcroppingError,
        onOutcropImage,
        isOutcroppingEnabled,
        updateSrc,
        src,
        shouldShowOutcropWarning,
        shouldRenderOutcropBanner,
        setShouldShowOutcropBanner,
        setOutcroppingStatus,
    };
};
